import { z } from 'zod';
import { userRoleSchema } from '../enums.js';
import { emailSchema, passwordSchema } from './auth.js';

/**
 * Account management, for administrators only.
 *
 * Kept in its own file for the same reason `admin.ts` is: everything here
 * carries an email address, and several shapes carry a soft-delete marker.
 * Neither belongs in a learner-facing response, and the surest way to keep them
 * out is for the learner schemas to have no reason to import this file.
 *
 * There is no schema here that returns a password hash. The API never reads one
 * out of the database for any purpose except verification at login.
 */

/** One row of the admin user list: identity plus the progress projection. */
export const adminUserRowSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  role: userRoleSchema,
  timezone: z.string(),
  createdAt: z.string(),
  /** Set when the account has been deactivated. Sign-in is refused while it is. */
  deletedAt: z.string().nullable(),
  totalXp: z.number().int(),
  level: z.number().int(),
  exercisesSolved: z.number().int(),
  currentStreak: z.number().int(),
  lastActivityDate: z.string().nullable(),
});
export type AdminUserRow = z.infer<typeof adminUserRowSchema>;

export const adminUserListQuerySchema = z.object({
  /** Matches display name or email, case-insensitively. */
  search: z.string().trim().max(120).optional(),
  role: userRoleSchema.optional(),
  /** Deactivated accounts are hidden unless asked for. */
  includeDeleted: z.coerce.boolean().default(false),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

export const adminUserListResponseSchema = z.object({
  items: z.array(adminUserRowSchema),
  total: z.number().int(),
});
export type AdminUserListResponse = z.infer<typeof adminUserListResponseSchema>;

/** The fuller picture behind one account. */
export const adminUserDetailSchema = adminUserRowSchema.extend({
  longestStreak: z.number().int(),
  lessonsCompleted: z.number().int(),
  totalAttempts: z.number().int(),
  correctAttempts: z.number().int(),
  /** Null rather than zero when they have never submitted anything. */
  accuracy: z.number().nullable(),
  groups: z.array(z.object({ id: z.uuid(), name: z.string() })),
  activeSessions: z.number().int(),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

export const createUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: z.string().trim().min(1).max(60),
  role: userRoleSchema.default('learner'),
  timezone: z.string().max(64).default('UTC'),
});
export type CreateUserRequest = z.infer<typeof createUserSchema>;

export const updateUserSchema = z
  .object({
    displayName: z.string().trim().min(1).max(60).optional(),
    role: userRoleSchema.optional(),
    timezone: z.string().max(64).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Nothing to update');
export type UpdateUserRequest = z.infer<typeof updateUserSchema>;

/**
 * An administrator setting somebody's password directly.
 *
 * There is no email in this deployment, so a self-service reset link is not
 * something this stack can send. The admin sets a password and passes it on out
 * of band. Every existing session for that account is revoked at the same time,
 * because the usual reason to reset a password is that the old one is no longer
 * trusted — leaving live sessions running would defeat the point.
 */
export const setUserPasswordSchema = z.object({
  password: passwordSchema,
});
export type SetUserPasswordRequest = z.infer<typeof setUserPasswordSchema>;

export const adminActionResultSchema = z.object({
  user: adminUserRowSchema,
  /** How many refresh-token families were revoked as a side effect. */
  sessionsRevoked: z.number().int(),
});
export type AdminActionResult = z.infer<typeof adminActionResultSchema>;
